import {
    expiryTypeDescriptionMap,
    IColDef,
    orderTypeDescriptionMap,
    priceAndPointsValueFormatter,
    sideCellValueFormatter,
} from '.';
import { filterComparatorForDate } from '../../helpers/ag-grid';
import { formatDatetime } from '@alk/dates';
import { IOrderStatus } from '../../interfaces';
import { orderStatusMapper, pendingStrategyMapper, spotTypeToTextMapper } from '../../helpers/mappers';

export const COL_DEF_STATUS: IColDef = {
    type: [ 'sleeve' ],
    headerName: 'Execution Status',
    field: 'state',
    cellRendererParams: {
        getSleeveValue: (value: IOrderStatus): string => orderStatusMapper[ value ],
        getSleeveClass: (value: string): string => `ag-sleeve-${ value }`,
    },
    width: 95,
    enableRowGroup: true,
};

export const COL_DEF_FILL_PERCENT: IColDef = {
    type: [ 'string' ],
    headerName: '% Fill',
    field: 'fillPercent',
    valueFormatter: params => `${ params?.value }%`,
    width: 60,
    enableRowGroup: true,
};

export const COL_DEF_SPOT_TYPE: IColDef = {
    type: [ 'string' ],
    headerName: 'Type',
    field: 'spotType',
    valueFormatter: params => spotTypeToTextMapper[ params?.value ],
    width: 90,
    enableRowGroup: true,
};

export const COL_DEF_PRODUCT: IColDef = {
    type: [ 'string' ],
    headerName: 'Type',
    field: 'orderType',
    valueFormatter: params => orderTypeDescriptionMap[ params?.value ],
    width: 90,
    enableRowGroup: true,
};

export const COL_DEF_ID: IColDef = {
    type: [ 'string' ],
    headerName: 'Id',
    field: 'orderId',
    width: 95,
    sort: 'desc',
    enableRowGroup: true,
};

export const COL_DEF_DEAL_NUMBER: IColDef = {
    type: [ 'string' ],
    headerName: 'Deal Number',
    field: 'dealNumber',
    width: 95,
    enableRowGroup: true,
};

export const COL_DEF_CCY_PAIR: IColDef = {
    type: [ 'string' ],
    headerName: 'CCY Pair',
    field: 'instrument',
    width: 80,
    enableRowGroup: true,
};

export const COL_DEF_SIDE: IColDef = {
    type: [ 'string' ],
    headerName: 'Side',
    field: 'side',
    width: 55,
    enableRowGroup: true,
};

export const COL_DEF_SIDE_SWAP: IColDef = {
    type: [ 'string' ],
    headerName: 'Side',
    field: 'side',
    valueFormatter: sideCellValueFormatter,
    width: 55,
    enableRowGroup: true,
};

export const COL_DEF_TRADER: IColDef = {
    type: [ 'string' ],
    headerName: 'Trader',
    field: 'traderName',
    width: 65,
    enableRowGroup: true,
};

export const COL_DEF_CLIENT: IColDef = {
    type: [ 'string' ],
    headerName: 'Client',
    field: 'clientName',
    width: 60,
    enableRowGroup: true,
};

export const COL_DEF_REQUESTED_AMOUNT: IColDef = {
    type: [ 'amount', 'amount_keyed-currency' ],
    headerName: 'Requested Amount',
    field: 'requestedAmount',
    width: 145,
    cellRendererParams: {
        currencyKey: 'baseCurrency',
    },
    enableRowGroup: true,
    colIdToAdd: 'requestedAmountCurrency',
};

export const COL_DEF_FILLED_AMOUNT: IColDef = {
    type: [ 'amount', 'amount_keyed-currency' ],
    headerName: 'Filled Amount',
    field: 'filledAmount',
    width: 110,
    cellRendererParams: {
        currencyKey: 'baseCurrency',
    },
    enableRowGroup: true,
    enableValue: true,
    colIdToAdd: 'filledAmountCurrency',
};

/* this column is used only in the export file */
export const COL_DEF_FILLED_AMOUNT_CURRENCY: IColDef = {
    type: [ 'string' ],
    headerName: 'Filled Amount Currency',
    colId: 'filledAmountCurrency',
    field: 'baseCurrency',
    suppressColumnsToolPanel: true,
    suppressFiltersToolPanel: true,
    hide: true,
};

/* this column is used only in the export file */
export const COL_DEF_REQUESTED_AMOUNT_CURRENCY: IColDef = {
    type: [ 'string' ],
    headerName: 'Requested Amount Currency',
    colId: 'requestedAmountCurrency',
    field: 'baseCurrency',
    suppressColumnsToolPanel: true,
    suppressFiltersToolPanel: true,
    hide: true,
};

/* this column is used only in the export file */
export const COL_DEF_LEFT_AMOUNT_CURRENCY: IColDef = {
    type: [ 'string' ],
    headerName: 'Left Amount Currency',
    colId: 'leftAmountCurrency',
    field: 'baseCurrency',
    suppressColumnsToolPanel: true,
    suppressFiltersToolPanel: true,
    hide: true,
};

/* this column is used only in the export file */
export const COL_DEF_NEAR_AMOUNT_CURRENCY: IColDef = {
    type: [ 'string' ],
    headerName: 'Near Amount (Base Ccy) Currency',
    colId: 'nearAmountCurrency',
    field: 'baseCurrency',
    suppressColumnsToolPanel: true,
    suppressFiltersToolPanel: true,
    hide: true,
};

/* this column is used only in the export file */
export const COL_DEF_FAR_AMOUNT_CURRENCY: IColDef = {
    type: [ 'string' ],
    headerName: 'Far Amount (Base Ccy) Currency',
    colId: 'farAmountCurrency',
    field: 'baseCurrency',
    suppressColumnsToolPanel: true,
    suppressFiltersToolPanel: true,
    hide: true,
};

export const COL_DEF_LEFT_AMOUNT: IColDef = {
    type: [ 'amount', 'amount_keyed-currency' ],
    headerName: 'Left Amount',
    field: 'leftAmount',
    width: 100,
    cellRendererParams: {
        currencyKey: 'baseCurrency',
    },
    enableRowGroup: true,
    enableValue: true,
    colIdToAdd: 'leftAmountCurrency',
};

export const COL_DEF_COUNTER_FILLED_AMOUNT: IColDef = {
    type: [ 'amount', 'amount_keyed-currency' ],
    headerName: 'Counter Filled Amount',
    field: 'counterFilledAmount',
    width: 155,
    cellRendererParams: {
        currencyKey: 'counterCurrency',
    },
    enableRowGroup: true,
    enableValue: true,
    colIdToAdd: 'counterFilledAmountCurrency',
};

export const COL_DEF_FAR_COUNTER_FILLED_AMOUNT: IColDef = {
    type: [ 'amount', 'amount_keyed-currency' ],
    headerName: 'Far Amount (Counter Ccy)',
    field: 'farCounterFilledAmount',
    width: 155,
    cellRendererParams: {
        currencyKey: 'counterCurrency',
    },
    enableRowGroup: true,
    enableValue: true,
    colIdToAdd: 'farCounterFilledAmountCurrency',
};

/* this column is used only in the export file */
export const COL_DEF_COUNTER_FILLED_AMOUNT_CURRENCY: IColDef = {
    type: [ 'string' ],
    headerName: 'Counter Filled Amount Currency',
    colId: 'counterFilledAmountCurrency',
    field: 'counterCurrency',
    suppressColumnsToolPanel: true,
    suppressFiltersToolPanel: true,
    hide: true,
};

export const COL_DEF_FAR_COUNTER_FILLED_AMOUNT_CURRENCY: IColDef = {
    type: [ 'string' ],
    headerName: 'Far Amount (Counter Ccy) Currency',
    colId: 'farCounterFilledAmountCurrency',
    field: 'counterCurrency',
    suppressColumnsToolPanel: true,
    suppressFiltersToolPanel: true,
    hide: true,
};

export const COL_DEF_REQUESTED_PRICE: IColDef = {
    type: [ 'rate' ],
    headerName: 'Requested Price',
    field: 'requestedPrice',
    valueFormatter: priceAndPointsValueFormatter,
    width: 125,
    enableRowGroup: true,
    enableValue: true,
};

export const COL_DEF_FILLED_PRICE: IColDef = {
    type: [ 'rate' ],
    headerName: 'Filled Price',
    field: 'filledPrice',
    valueFormatter: priceAndPointsValueFormatter,
    width: 90,
    enableRowGroup: true,
    enableValue: true,
};

export const COL_DEF_VALUE_DATE: IColDef = {
    type: [ 'date' ],
    headerName: 'Value Date',
    field: 'valueDate',
    width: 90,
    enableRowGroup: true,
    filter: 'agDateColumnFilter',
    filterParams: {
        comparator: filterComparatorForDate,
    },
};

export const COL_DEF_CREATED_DATE: IColDef = {
    type: [ 'date' ],
    headerName: 'Created Date',
    field: 'tradeDate',
    width: 200,
    valueFormatter: (params) => {
        return Boolean(params.value) ? formatDatetime(Date.parse(params.value)) : '';
    },
    enableRowGroup: true,
    filter: 'agDateColumnFilter',
    filterParams: {
        comparator: filterComparatorForDate,
    },
};

export const COL_DEF_EXECUTION_DATE: IColDef = {
    type: [ 'date' ],
    headerName: 'Execution Date',
    field: 'executionDate',
    width: 115,
    valueFormatter: (params) => {
        return Boolean(params.value) ? formatDatetime(Date.parse(params.value)) : '';
    },
    enableRowGroup: true,
    filter: 'agDateColumnFilter',
    filterParams: {
        comparator: filterComparatorForDate,
    },
};

export const COL_DEF_NEAR_AMOUNT: IColDef = {
    type: [ 'amount', 'amount_keyed-currency' ],
    headerName: 'Near Amount (Base Ccy)',
    field: 'nearAmount',
    width: 100,
    cellRendererParams: {
        currencyKey: 'baseCurrency',
    },
    enableRowGroup: true,
    enableValue: true,
    colIdToAdd: 'nearAmountCurrency',
};

export const COL_DEF_NEAR_PRICE: IColDef = {
    type: [ 'rate' ],
    headerName: 'Near Price',
    field: 'nearPrice',
    valueFormatter: priceAndPointsValueFormatter,
    width: 90,
    enableRowGroup: true,
    enableValue: true,
};

export const COL_DEF_NEAR_VALUE_DATE: IColDef = {
    type: [ 'date' ],
    headerName: 'Near Value Date',
    field: 'nearValueDate',
    width: 90,
    enableRowGroup: true,
    filter: 'agDateColumnFilter',
    filterParams: {
        comparator: filterComparatorForDate,
    },
};

export const COL_DEF_NEAR_POINTS: IColDef = {
    type: [ 'rate' ],
    headerName: 'Near Points',
    field: 'nearPoints',
    valueFormatter: priceAndPointsValueFormatter,
    width: 90,
    enableRowGroup: true,
    enableValue: true,
};

export const COL_DEF_FAR_AMOUNT: IColDef = {
    type: [ 'amount', 'amount_keyed-currency' ],
    headerName: 'Far Amount (Base Ccy)',
    field: 'farAmount',
    width: 100,
    cellRendererParams: {
        currencyKey: 'baseCurrency',
    },
    enableRowGroup: true,
    enableValue: true,
    colIdToAdd: 'farAmountCurrency',
};

export const COL_DEF_FAR_PRICE: IColDef = {
    type: [ 'rate' ],
    headerName: 'Far Price',
    field: 'farPrice',
    valueFormatter: priceAndPointsValueFormatter,
    width: 90,
    enableRowGroup: true,
    enableValue: true,
};

export const COL_DEF_FAR_VALUE_DATE: IColDef = {
    type: [ 'date' ],
    headerName: 'Far Value Date',
    field: 'farValueDate',
    width: 90,
    enableRowGroup: true,
    filter: 'agDateColumnFilter',
    filterParams: {
        comparator: filterComparatorForDate,
    },
};

export const COL_DEF_FAR_POINTS: IColDef = {
    type: [ 'rate' ],
    headerName: 'Far Points',
    field: 'farPoints',
    valueFormatter: priceAndPointsValueFormatter,
    width: 90,
    enableRowGroup: true,
    enableValue: true,
};

export const COL_DEF_SPOT_RATE: IColDef = {
    type: [ 'rate' ],
    headerName: 'Spot Rate',
    field: 'spotRate',
    valueFormatter: priceAndPointsValueFormatter,
    width: 90,
    enableRowGroup: true,
    enableValue: true,
};

export const COL_DEF_MKT_PERCENT: IColDef = {
    type: [ 'string' ],
    headerName: '% to Market',
    field: 'marketDistance',
    width: 65,
    hide: true,
    enableRowGroup: true,
};

export const COL_DEF_MKT_POINTS: IColDef = {
    type: [ 'string' ],
    headerName: 'Pts to Market',
    field: 'marketPoints',
    width: 65,
    hide: true,
    enableRowGroup: true,
};

export const COL_DEF_EXPIRY_TYPE: IColDef = {
    type: [ 'string' ],
    headerName: 'Expiry',
    field: 'expiryType',
    valueFormatter: params => expiryTypeDescriptionMap[ params?.value ],
    width: 65,
    enableRowGroup: true,
};

export const COL_DEF_EXPIRY_DATE: IColDef = {
    type: [ 'date' ],
    headerName: 'Expiry Date',
    field: 'expiryDate',
    width: 65,
    enableRowGroup: true,
    filter: 'agDateColumnFilter',
    filterParams: {
        comparator: filterComparatorForDate,
    },
};

export const COL_DEF_PENDING_STRATEGY_ID: IColDef = {
    type: [ 'string' ],
    headerName: 'Strategy Id',
    field: 'pendingStrategyId',
    width: 90,
    enableRowGroup: true,
};

export const COL_DEF_PENDING_STRATEGY: IColDef = {
    type: [ 'string' ],
    headerName: 'Strategy',
    field: 'pendingStrategy',
    valueFormatter: params => pendingStrategyMapper[ params?.value ],
    width: 65,
    enableRowGroup: true,
};

export const COL_DEF_PENDING_LEG_TYPE: IColDef = {
    type: [ 'string' ],
    headerName: 'Type',
    field: 'pendingLegType',
    valueFormatter: params => spotTypeToTextMapper[ params?.value ],
    width: 65,
    enableRowGroup: true,
};
